import { useState, useEffect, useCallback, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";
import {
  Shield, ShieldCheck, ShieldAlert, CheckCircle, Clock, AlertTriangle,
  FileText, FolderOpen, Calendar, RefreshCw, ChevronRight, X, BookOpen,
  TrendingUp, Filter, Search, Zap, BarChart3, Eye, ChevronDown,
  Upload, Download, Trash2, Loader2,
} from "lucide-react";
import { API_URL } from "../config/api";
import { Navbar } from "../components/Navbar";

const CATEGORY_LABELS = {
  hr_labor: "HR / Delovno pravo",
  records: "Evidence in zapisi",
  data_protection: "Varstvo podatkov",
  health_safety: "Varnost in zdravje",
  governance: "Upravljanje podjetja",
};

const CATEGORY_COLORS = {
  hr_labor: "bg-blue-100 text-blue-800 border-blue-200",
  records: "bg-purple-100 text-purple-800 border-purple-200",
  data_protection: "bg-teal-100 text-teal-800 border-teal-200",
  health_safety: "bg-orange-100 text-orange-800 border-orange-200",
  governance: "bg-slate-100 text-slate-800 border-slate-200",
};

const STATUS_CONFIG = {
  compliant: { label: "Skladno", color: "bg-green-100 text-green-700", icon: CheckCircle },
  due_soon: { label: "Kmalu zapade", color: "bg-amber-100 text-amber-700", icon: Clock },
  overdue: { label: "Zamujeno", color: "bg-red-100 text-red-700", icon: AlertTriangle },
  in_review: { label: "V pregledu", color: "bg-indigo-100 text-indigo-700", icon: Eye },
  blocked: { label: "Blokirano", color: "bg-red-100 text-red-700", icon: ShieldAlert },
  draft: { label: "Osnutek", color: "bg-slate-100 text-slate-600", icon: FileText },
};

const REVIEW_TYPE_LABELS = {
  annual: "Letni",
  event_driven: "Ob dogodku",
  both: "Letni + dogodek",
};

const TRIGGER_LABELS = {
  new_law: "Nov zakon",
  collective_agreement_change: "Kolektivna pogodba",
  org_change: "Organizacija",
  new_work_model: "Model dela",
  new_data_processing: "Osebni podatki",
  safety_risk_change: "Varnostna tveganja",
  new_site: "Nova lokacija",
  new_equipment: "Nova oprema",
  incident: "Incident",
  process_change: "Sprememba procesa",
};

const MONTHS_SL = ["Jan", "Feb", "Mar", "Apr", "Maj", "Jun", "Jul", "Avg", "Sep", "Okt", "Nov", "Dec"];

export default function CompliancePage() {
  const [token] = useState(() => localStorage.getItem("token"));
  const [overview, setOverview] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [calendar, setCalendar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [missingTemplateOnly, setMissingTemplateOnly] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchOverview = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/compliance/overview`, { headers });
      setOverview(res.data);
    } catch (err) { console.error(err); }
  }, []);

  const fetchDocuments = useCallback(async () => {
    try {
      let url = `${API_URL}/compliance/documents?limit=200`;
      if (categoryFilter) url += `&category=${categoryFilter}`;
      if (statusFilter) url += `&status=${statusFilter}`;
      const res = await axios.get(url, { headers });
      setDocuments(res.data.documents || []);
    } catch (err) { console.error(err); }
  }, [categoryFilter, statusFilter]);

  const fetchReviews = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/compliance/reviews?limit=100`, { headers });
      setReviews(res.data.reviews || []);
    } catch (err) { console.error(err); }
  }, []);

  const fetchCalendar = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/compliance/calendar`, { headers });
      setCalendar(res.data);
    } catch (err) { console.error(err); }
  }, []);

  const fetchDocDetail = async (docId) => {
    try {
      const res = await axios.get(`${API_URL}/compliance/documents/${docId}`, { headers });
      setSelectedDoc(res.data);
    } catch (err) { toast.error("Napaka pri nalaganju"); }
  };

  const refreshAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchOverview(), fetchDocuments(), fetchReviews(), fetchCalendar()]);
    setLoading(false);
  }, [fetchOverview, fetchDocuments, fetchReviews, fetchCalendar]);

  useEffect(() => { refreshAll(); }, [refreshAll]);

  useEffect(() => { fetchDocuments(); }, [fetchDocuments]);

  const handleCompleteReview = async (reviewId, outcome) => {
    try {
      await axios.post(`${API_URL}/compliance/reviews/${reviewId}/complete`, {
        outcome,
        reviewer: "admin",
        notes: outcome === "no_change_confirmed" ? "Brez sprememb" : "Posodobljeno",
      }, { headers });
      toast.success(`Pregled zakljucen: ${outcome}`);
      refreshAll();
    } catch (err) { toast.error("Napaka"); }
  };

  const stats = overview?.stats || {};

  return (
    <div className="min-h-screen bg-slate-50" data-testid="compliance-page">
      <Navbar />
      <main className="container mx-auto px-4 py-6 pt-20">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3" data-testid="compliance-title">
              <Shield className="h-7 w-7 text-emerald-600" />
              ComplianceBot Nadzorna Plosca
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Register skladnosti | Pregledi | Opomniki | Koledar | Sprozilci sprememb
            </p>
          </div>
          <Button variant="outline" onClick={refreshAll} disabled={loading} data-testid="refresh-compliance">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Osvezi
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
          <StatCard label="Skupaj dokumentov" value={stats.total || 0} icon={FileText} iconColor="text-slate-600" bgColor="bg-slate-100" testId="stat-total" />
          <StatCard label="Skladno" value={stats.compliant || 0} icon={ShieldCheck} iconColor="text-green-600" bgColor="bg-green-100" testId="stat-compliant" />
          <StatCard label="Kmalu zapade" value={stats.due_soon || 0} icon={Clock} iconColor="text-amber-600" bgColor="bg-amber-100" testId="stat-due" highlight />
          <StatCard label="Zamujeno" value={stats.overdue || 0} icon={AlertTriangle} iconColor="text-red-600" bgColor="bg-red-100" testId="stat-overdue" highlight="red" />
          <StatCard label="V pregledu" value={stats.in_review + (stats.open_reviews || 0)} icon={Eye} iconColor="text-indigo-600" bgColor="bg-indigo-100" testId="stat-review" />
          <StatCard label="Skladnost %" value={`${stats.completion_pct || 0}%`} icon={TrendingUp} iconColor="text-emerald-600" bgColor="bg-emerald-100" testId="stat-pct" />
        </div>

        <Tabs defaultValue="overview" className="space-y-4">
          <TabsList className="bg-white border flex-wrap">
            <TabsTrigger value="overview" className="gap-2" data-testid="tab-comp-overview">
              <BarChart3 className="h-4 w-4" /> Pregled
            </TabsTrigger>
            <TabsTrigger value="register" className="gap-2" data-testid="tab-comp-register">
              <BookOpen className="h-4 w-4" /> Register
              <span className="ml-1 px-1.5 py-0.5 text-xs bg-slate-200 text-slate-700 rounded-full">{stats.total || 0}</span>
            </TabsTrigger>
            <TabsTrigger value="reviews" className="gap-2" data-testid="tab-comp-reviews">
              <Eye className="h-4 w-4" /> Vrsta pregledov
              {reviews.filter(r => r.status !== "completed").length > 0 && (
                <span className="ml-1 px-1.5 py-0.5 text-xs bg-amber-500 text-white rounded-full">
                  {reviews.filter(r => r.status !== "completed").length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="calendar" className="gap-2" data-testid="tab-comp-calendar">
              <Calendar className="h-4 w-4" /> Koledar
            </TabsTrigger>
          </TabsList>

          {/* OVERVIEW TAB */}
          <TabsContent value="overview" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Due windows */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Clock className="h-4 w-4 text-amber-500" /> Prihodnji pregledi
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <DueBar label="V 30 dneh" count={stats.due_30 || 0} total={stats.total || 1} color="bg-red-500" />
                    <DueBar label="V 60 dneh" count={stats.due_60 || 0} total={stats.total || 1} color="bg-amber-500" />
                    <DueBar label="V 90 dneh" count={stats.due_90 || 0} total={stats.total || 1} color="bg-yellow-400" />
                  </div>
                </CardContent>
              </Card>

              {/* By category */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <FolderOpen className="h-4 w-4 text-slate-600" /> Po kategorijah
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {Object.entries(overview?.by_category || {}).map(([cat, count]) => (
                      <div key={cat} className="flex items-center justify-between">
                        <span className={`text-xs px-2 py-1 rounded border ${CATEGORY_COLORS[cat] || "bg-slate-100"}`}>
                          {CATEGORY_LABELS[cat] || cat}
                        </span>
                        <span className="font-bold text-sm">{count}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Overdue items */}
            {(overview?.overdue_docs || []).length > 0 && (
              <Card className="border-red-200">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2 text-red-700">
                    <AlertTriangle className="h-4 w-4" /> Zamujeni dokumenti ({overview.overdue_docs.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {overview.overdue_docs.map((d, i) => (
                      <DocRow key={i} doc={d} onClick={() => fetchDocDetail(d.doc_id)} />
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Due soon */}
            {(overview?.due_soon_docs || []).length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Clock className="h-4 w-4 text-amber-500" /> Kmalu zapadejo ({overview.due_soon_docs.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {overview.due_soon_docs.map((d, i) => (
                      <DocRow key={i} doc={d} onClick={() => fetchDocDetail(d.doc_id)} />
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Recent triggers */}
            {(overview?.recent_triggers || []).length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Zap className="h-4 w-4 text-indigo-500" /> Nedavni sprozilci sprememb
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {overview.recent_triggers.map((t, i) => (
                      <div key={i} className="flex items-center gap-3 p-2 rounded border hover:bg-slate-50 text-sm">
                        <Zap className="h-4 w-4 text-indigo-400 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <span className="text-xs bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded mr-2">
                            {TRIGGER_LABELS[t.event_type] || t.event_type}
                          </span>
                          <span className="text-slate-700">{t.description}</span>
                        </div>
                        <span className="text-xs text-slate-400">{new Date(t.triggered_at).toLocaleDateString("sl-SI")}</span>
                        <span className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">{(t.reviews_created || []).length} pregledov</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* REGISTER TAB */}
          <TabsContent value="register" className="space-y-4">
            {/* Filters */}
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-slate-400" />
                <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}
                  className="text-sm border rounded px-2 py-1.5 bg-white" data-testid="filter-category">
                  <option value="">Vse kategorije</option>
                  {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
                className="text-sm border rounded px-2 py-1.5 bg-white" data-testid="filter-status">
                <option value="">Vsi statusi</option>
                {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
              <button
                type="button"
                onClick={() => setMissingTemplateOnly(v => !v)}
                className={`text-sm rounded px-2.5 py-1.5 border inline-flex items-center gap-1.5 transition-colors ${
                  missingTemplateOnly
                    ? "bg-amber-100 text-amber-800 border-amber-300"
                    : "bg-white text-slate-600 hover:bg-slate-50 border-slate-300"
                }`}
                data-testid="filter-missing-templates"
                title="Pokaži samo dokumente brez urejivih predlog (PDF/DOC)"
              >
                <AlertTriangle className="h-4 w-4" />
                {missingTemplateOnly ? "Samo manjkajoče predloge" : "Manjkajoče predloge"}
                {missingTemplateOnly && (
                  <span className="ml-1 text-xs bg-amber-200 text-amber-900 px-1.5 rounded">
                    {documents.filter(d => (d.editable_files_count || 0) === 0).length}
                  </span>
                )}
              </button>
              <span className="text-xs text-slate-400">{documents.length} dokumentov</span>
            </div>

            {/* Group by category */}
            {Object.entries(CATEGORY_LABELS).map(([cat, catLabel]) => {
              const catDocs = documents.filter(d =>
                d.category === cat && (!missingTemplateOnly || (d.editable_files_count || 0) === 0)
              );
              if (catDocs.length === 0) return null;
              return (
                <Card key={cat}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <span className={`px-2 py-0.5 rounded text-xs border ${CATEGORY_COLORS[cat]}`}>{catLabel}</span>
                      <span className="text-slate-400 font-normal">({catDocs.length})</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="space-y-1">
                      {catDocs.map((d, i) => (
                        <DocRow key={i} doc={d} onClick={() => fetchDocDetail(d.doc_id)} showFull />
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </TabsContent>

          {/* REVIEWS TAB */}
          <TabsContent value="reviews" className="space-y-4">
            {reviews.length === 0 ? (
              <Card><CardContent className="py-12 text-center text-slate-400">
                <Eye className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p className="text-sm">Ni odprtih pregledov.</p>
              </CardContent></Card>
            ) : (
              <>
                {/* Open/In progress */}
                {reviews.filter(r => r.status !== "completed").length > 0 && (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base">Odprti pregledi ({reviews.filter(r => r.status !== "completed").length})</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {reviews.filter(r => r.status !== "completed").map((r, i) => (
                          <ReviewCard key={i} review={r} onComplete={handleCompleteReview} onViewDoc={() => fetchDocDetail(r.doc_id)} />
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Completed */}
                {reviews.filter(r => r.status === "completed").length > 0 && (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base text-slate-500">Zakljuceni pregledi ({reviews.filter(r => r.status === "completed").length})</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {reviews.filter(r => r.status === "completed").slice(0, 20).map((r, i) => (
                          <div key={i} className="flex items-center gap-3 p-2 rounded border text-sm opacity-70">
                            <CheckCircle className="h-4 w-4 text-green-500" />
                            <span className="font-mono text-xs text-slate-400">{r.review_id}</span>
                            <span className="truncate flex-1">{r.title_sl}</span>
                            <span className="text-xs bg-green-50 text-green-600 px-2 py-0.5 rounded">{r.outcome}</span>
                            <span className="text-xs text-slate-400">{new Date(r.completed_at).toLocaleDateString("sl-SI")}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </TabsContent>

          {/* CALENDAR TAB */}
          <TabsContent value="calendar" className="space-y-4">
            {calendar ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(calendar.calendar).map(([month, items]) => {
                  const monthIdx = parseInt(month) - 1;
                  const isCurrentMonth = new Date().getMonth() === monthIdx && new Date().getFullYear() === calendar.year;
                  return (
                    <Card key={month} className={`${isCurrentMonth ? "ring-2 ring-emerald-500" : ""} ${items.length === 0 ? "opacity-50" : ""}`}>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center justify-between">
                          <span className={isCurrentMonth ? "text-emerald-700 font-bold" : ""}>
                            {MONTHS_SL[monthIdx]} {calendar.year}
                          </span>
                          {items.length > 0 && (
                            <span className="text-xs bg-slate-100 px-2 py-0.5 rounded">{items.length} pregledov</span>
                          )}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0">
                        {items.length === 0 ? (
                          <p className="text-xs text-slate-400">Brez obveznosti</p>
                        ) : (
                          <div className="space-y-1">
                            {items.map((item, i) => {
                              const sc = STATUS_CONFIG[item.status] || STATUS_CONFIG.compliant;
                              return (
                                <div key={i} className="flex items-center gap-2 text-xs p-1.5 rounded hover:bg-slate-50 cursor-pointer"
                                  onClick={() => fetchDocDetail(item.doc_id)}>
                                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${item.status === "overdue" ? "bg-red-500" : item.status === "due_soon" ? "bg-amber-500" : "bg-green-500"}`} />
                                  <span className="truncate flex-1">{item.title_sl}</span>
                                  <span className="text-slate-400">{item.due_date?.slice(8)}</span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <p className="text-center text-slate-400 py-8">Nalaganje koledarja...</p>
            )}
          </TabsContent>
        </Tabs>

        {/* Document Detail Modal */}
        {selectedDoc && (
          <DocDetailModal doc={selectedDoc} onClose={() => setSelectedDoc(null)} />
        )}
      </main>
    </div>
  );
}

// ─── Sub-components ───

function StatCard({ label, value, icon: Icon, iconColor, bgColor, testId, highlight }) {
  return (
    <Card data-testid={testId}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wider">{label}</p>
            <p className={`text-2xl font-bold mt-1 ${highlight === "red" ? "text-red-600" : highlight ? "text-amber-600" : ""}`}>{value}</p>
          </div>
          <div className={`p-2 rounded-lg ${bgColor}`}><Icon className={`h-5 w-5 ${iconColor}`} /></div>
        </div>
      </CardContent>
    </Card>
  );
}

function DueBar({ label, count, total, color }) {
  const pct = Math.min(100, Math.round((count / total) * 100));
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm">
        <span className="text-slate-600">{label}</span>
        <span className="font-bold">{count}</span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color} transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function DocRow({ doc, onClick, showFull }) {
  const sc = STATUS_CONFIG[doc.status] || STATUS_CONFIG.compliant;
  return (
    <div className="flex items-center gap-3 p-2.5 rounded-lg border hover:bg-slate-50 transition-colors cursor-pointer"
      data-testid={`comp-doc-${doc.doc_id}`} onClick={onClick}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
          <span className="font-mono text-xs text-slate-400">{doc.doc_id}</span>
          <span className={`px-1.5 py-0.5 rounded text-xs ${sc.color}`}>{sc.label}</span>
          <span className="text-xs bg-slate-50 text-slate-500 px-1.5 py-0.5 rounded border">
            {REVIEW_TYPE_LABELS[doc.review_type] || doc.review_type}
          </span>
          {showFull && (
            <span className="text-xs text-slate-400">Lastnik: {doc.owner}</span>
          )}
          {doc.editable_files_count === 0 && (
            <span
              className="text-xs bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded inline-flex items-center gap-1"
              title="Manjka urejivi predlogi (PDF/DOC) – pripeti pred naslednjim opomnikom"
              data-testid={`comp-no-template-${doc.doc_id}`}
            >
              <AlertTriangle className="h-3 w-3" />
              Manjka predloga
            </span>
          )}
        </div>
        <p className="text-sm font-medium truncate">{doc.title_sl}</p>
        {showFull && doc.legal_basis && (
          <p className="text-xs text-slate-400 truncate">{doc.legal_basis}</p>
        )}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {doc.next_review_date && (
          <span className="text-xs text-slate-400 whitespace-nowrap">{doc.next_review_date}</span>
        )}
        <ChevronRight className="h-4 w-4 text-slate-300" />
      </div>
    </div>
  );
}

function ReviewCard({ review, onComplete, onViewDoc }) {
  const isOverdue = review.due_at && new Date(review.due_at) < new Date();
  return (
    <div className={`border rounded-lg p-4 ${isOverdue ? "border-red-200 bg-red-50/50" : "bg-white"}`} data-testid={`review-${review.review_id}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="font-mono text-xs text-slate-500">{review.review_id}</span>
          <span className={`px-1.5 py-0.5 rounded text-xs ${review.review_type === "event_driven" ? "bg-indigo-100 text-indigo-700" : "bg-blue-100 text-blue-700"}`}>
            {REVIEW_TYPE_LABELS[review.review_type] || review.review_type}
          </span>
          {isOverdue && <span className="px-1.5 py-0.5 rounded text-xs bg-red-100 text-red-700">ZAMUJENO</span>}
          {review.trigger_event_type && (
            <span className="px-1.5 py-0.5 rounded text-xs bg-purple-100 text-purple-700">
              {TRIGGER_LABELS[review.trigger_event_type] || review.trigger_event_type}
            </span>
          )}
        </div>
        <span className="text-xs text-slate-400">Rok: {review.due_at}</span>
      </div>
      <p className="font-medium text-sm">{review.title_sl}</p>
      <p className="text-xs text-slate-500 mt-1">
        <span className={`px-1.5 py-0.5 rounded border ${CATEGORY_COLORS[review.category] || ""}`}>
          {CATEGORY_LABELS[review.category] || review.category}
        </span>
        {" "}Lastnik: {review.owner}
      </p>
      {review.trigger_description && (
        <p className="text-xs text-indigo-600 mt-1">Razlog: {review.trigger_description}</p>
      )}
      <div className="flex gap-2 mt-3">
        <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => onComplete(review.review_id, "updated")} data-testid={`review-updated-${review.review_id}`}>
          <CheckCircle className="h-3 w-3 mr-1" /> Posodobljeno
        </Button>
        <Button size="sm" variant="outline" onClick={() => onComplete(review.review_id, "no_change_confirmed")} data-testid={`review-nochange-${review.review_id}`}>
          Brez sprememb
        </Button>
        <Button size="sm" variant="ghost" onClick={onViewDoc}>
          <Eye className="h-3 w-3 mr-1" /> Dokument
        </Button>
      </div>
    </div>
  );
}

function DocDetailModal({ doc, onClose }) {
  const sc = STATUS_CONFIG[doc.status] || STATUS_CONFIG.compliant;
  const [showVersions, setShowVersions] = useState(false);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose} data-testid="compliance-detail-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-slate-50">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-sm font-bold text-emerald-700">{doc.doc_id}</span>
              <span className={`px-2 py-0.5 rounded text-xs ${sc.color}`}>{sc.label}</span>
              <span className={`px-2 py-0.5 rounded text-xs border ${CATEGORY_COLORS[doc.category] || ""}`}>
                {CATEGORY_LABELS[doc.category] || doc.category}
              </span>
              <span className="text-xs bg-slate-100 px-2 py-0.5 rounded">v{doc.current_version}</span>
            </div>
            <p className="text-sm font-medium mt-1">{doc.title_sl}</p>
            {doc.title_en && <p className="text-xs text-slate-400">{doc.title_en}</p>}
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-lg" data-testid="close-compliance-detail">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto max-h-[calc(85vh-80px)] px-6 py-4 space-y-5">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <DetailField label="Lastnik" value={doc.owner} />
            <DetailField label="Pravna podlaga" value={doc.legal_basis} />
            <DetailField label="Velja za" value={doc.applies_to} />
            <DetailField label="Tip pregleda" value={REVIEW_TYPE_LABELS[doc.review_type] || doc.review_type} />
            <DetailField label="Naslednji pregled" value={doc.next_review_date} />
            <DetailField label="Frekvenca" value={`${doc.review_frequency_months} mesecev`} />
            <DetailField label="Zadnji pregled" value={doc.last_reviewed_at ? new Date(doc.last_reviewed_at).toLocaleDateString("sl-SI") : "Se ni bil"} />
            <DetailField label="Zadnja odobritev" value={doc.last_approved_at ? new Date(doc.last_approved_at).toLocaleDateString("sl-SI") : "Se ni bila"} />
          </div>

          {doc.retention_rule && (
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Pravilo hrambe</p>
              <p className="text-sm bg-amber-50 border border-amber-200 rounded-lg p-2">{doc.retention_rule}</p>
            </div>
          )}

          {doc.notes && (
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Opombe</p>
              <p className="text-sm bg-slate-50 border rounded-lg p-2">{doc.notes}</p>
            </div>
          )}

          {/* File repository */}
          <ComplianceFilesSection docId={doc.doc_id} token={localStorage.getItem("token")} />

          {/* Trigger events */}
          {doc.trigger_events?.length > 0 && (
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Sprozilci sprememb</p>
              <div className="flex flex-wrap gap-2">
                {doc.trigger_events.map((t, i) => (
                  <span key={i} className="px-2 py-1 bg-indigo-50 text-indigo-700 rounded text-xs border border-indigo-200">
                    {TRIGGER_LABELS[t] || t}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Approval path */}
          {doc.approval_path?.length > 0 && (
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Pot odobritve</p>
              <div className="flex items-center gap-1">
                {doc.approval_path.map((step, i) => (
                  <span key={i} className="flex items-center gap-1">
                    <span className="px-2 py-1 bg-emerald-50 text-emerald-700 rounded text-xs border border-emerald-200">{step}</span>
                    {i < doc.approval_path.length - 1 && <ChevronRight className="h-3 w-3 text-slate-300" />}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Version history */}
          {(doc.versions || []).length > 0 && (
            <div>
              <button onClick={() => setShowVersions(!showVersions)}
                className="flex items-center gap-1 text-xs text-slate-500 uppercase tracking-wider mb-2 hover:text-slate-700">
                <ChevronDown className={`h-3 w-3 transition-transform ${showVersions ? "rotate-180" : ""}`} />
                Zgodovina verzij ({doc.versions.length})
              </button>
              {showVersions && (
                <div className="space-y-2 border-l-2 border-slate-200 ml-2">
                  {doc.versions.map((v, i) => (
                    <div key={i} className="pl-4 pb-2 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs font-bold">v{v.version}</span>
                        <span className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">{v.outcome}</span>
                        <span className="text-xs text-slate-400">{new Date(v.created_at).toLocaleDateString("sl-SI")}</span>
                      </div>
                      {v.notes && <p className="text-xs text-slate-500 mt-0.5">{v.notes}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Review history */}
          {(doc.reviews || []).length > 0 && (
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Zgodovina pregledov ({doc.reviews.length})</p>
              <div className="space-y-2">
                {doc.reviews.map((r, i) => (
                  <div key={i} className="flex items-center gap-3 p-2 rounded border text-sm">
                    <span className="font-mono text-xs text-slate-400">{r.review_id}</span>
                    <span className={`px-1.5 py-0.5 rounded text-xs ${r.status === "completed" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                      {r.status}
                    </span>
                    {r.outcome && <span className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">{r.outcome}</span>}
                    <span className="text-xs text-slate-400 ml-auto">{new Date(r.opened_at).toLocaleDateString("sl-SI")}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DetailField({ label, value }) {
  return (
    <div>
      <span className="text-slate-500 text-xs">{label}</span>
      <p className="font-medium text-sm">{value || "—"}</p>
    </div>
  );
}


// ─── Compliance file repository (drag/drop + tagged dropdown) ───
const COMPLIANCE_TAG_BADGE = {
  editable_pdf: "bg-blue-100 text-blue-800 border-blue-200",
  editable_doc: "bg-blue-100 text-blue-800 border-blue-200",
  editable_other: "bg-blue-50 text-blue-700 border-blue-200",
  signed_pdf: "bg-emerald-100 text-emerald-800 border-emerald-200",
  stamped_pdf: "bg-emerald-100 text-emerald-800 border-emerald-200",
  approved_pdf: "bg-emerald-100 text-emerald-800 border-emerald-200",
  scan: "bg-emerald-50 text-emerald-700 border-emerald-200",
  annex: "bg-purple-50 text-purple-700 border-purple-200",
  translation: "bg-purple-50 text-purple-700 border-purple-200",
  reviewer_notes: "bg-amber-50 text-amber-700 border-amber-200",
  evidence: "bg-amber-50 text-amber-700 border-amber-200",
  external_reference: "bg-slate-100 text-slate-700 border-slate-200",
  other: "bg-slate-100 text-slate-700 border-slate-200",
};

function _formatBytes(bytes) {
  if (!bytes) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function ComplianceFilesSection({ docId, token }) {
  const [categories, setCategories] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [tag, setTag] = useState("editable_pdf");
  const [description, setDescription] = useState("");
  const [versionLabel, setVersionLabel] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [cats, list] = await Promise.all([
        axios.get(`${API_URL}/compliance/file-categories`),
        axios.get(`${API_URL}/compliance/documents/${docId}/files`, { headers }),
      ]);
      setCategories(cats.data.categories || []);
      setFiles(list.data.files || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load files");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docId, token]);

  useEffect(() => { refresh(); }, [refresh]);

  const accept = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("tag", tag);
      if (description) fd.append("description", description);
      if (versionLabel) fd.append("version_label", versionLabel);
      const r = await axios.post(`${API_URL}/compliance/documents/${docId}/files`, fd, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      toast.success(`Uploaded "${r.data.file.filename}"`);
      setDescription("");
      setVersionLabel("");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const files = e.dataTransfer?.files;
    if (!files || files.length === 0) {
      toast.error(
        "Couldn't read that drop — if you dragged from another app, save the file to your desktop first.",
        { duration: 6000 }
      );
      return;
    }
    accept(files[0]);
  };

  const handleDownload = async (f) => {
    try {
      const r = await axios.get(
        `${API_URL}/compliance/documents/${docId}/files/${f.file_id}/download`,
        { headers, responseType: "blob" }
      );
      const blob = new Blob([r.data], { type: f.content_type });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = f.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Download failed");
    }
  };

  const handleDelete = async (f) => {
    if (!window.confirm(`Delete "${f.filename}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/compliance/documents/${docId}/files/${f.file_id}`, { headers });
      toast.success("File deleted");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const grouped = files.reduce((acc, f) => {
    (acc[f.tag] = acc[f.tag] || []).push(f);
    return acc;
  }, {});

  return (
    <div data-testid="compliance-files-section">
      <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">
        Datoteke / Files ({files.length})
      </p>

      {/* Tag picker + metadata */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3">
        <select
          value={tag}
          onChange={(e) => setTag(e.target.value)}
          className="h-9 px-2 rounded-md border border-slate-300 text-sm bg-white"
          data-testid="compliance-file-tag"
        >
          {categories.map((c) => (
            <optgroup key={c.label} label={c.label}>
              {c.tags.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </optgroup>
          ))}
        </select>
        <input
          value={versionLabel}
          onChange={(e) => setVersionLabel(e.target.value)}
          placeholder="Version label (e.g. v2 signed 09.05.2026)"
          className="h-9 px-2 rounded-md border border-slate-300 text-sm"
        />
        <input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Description (optional)"
          className="h-9 px-2 rounded-md border border-slate-300 text-sm"
        />
      </div>

      {/* Drop zone */}
      <div
        onClick={() => fileInputRef.current?.click()}
        onDrop={onDrop}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        className={`border-2 border-dashed rounded-lg p-5 text-center cursor-pointer transition-colors ${
          dragOver ? "border-emerald-500 bg-emerald-50" : "border-slate-300 hover:border-slate-400 bg-slate-50"
        }`}
        data-testid="compliance-file-dropzone"
      >
        {uploading ? (
          <div className="flex flex-col items-center gap-1">
            <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
            <p className="text-xs text-slate-600">Uploading…</p>
          </div>
        ) : (
          <>
            <Upload className="h-7 w-7 mx-auto text-slate-400 mb-1" />
            <p className="text-sm font-medium text-slate-700">Drag & drop documents here</p>
            <p className="text-xs text-slate-500">or click to browse · accepts PDF, DOC, DOCX, scans</p>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={(e) => { accept(e.target.files?.[0]); e.target.value = ""; }}
              data-testid="compliance-file-input"
            />
          </>
        )}
      </div>

      {/* Stored files grouped by tag */}
      <div className="mt-3">
        {loading ? (
          <div className="text-center py-4"><Loader2 className="h-5 w-5 mx-auto animate-spin text-slate-400" /></div>
        ) : files.length === 0 ? (
          <div className="text-center py-4 text-sm text-slate-500 italic">No files attached yet.</div>
        ) : (
          <div className="space-y-2">
            {Object.entries(grouped).map(([t, list]) => {
              const cls = COMPLIANCE_TAG_BADGE[t] || COMPLIANCE_TAG_BADGE.other;
              return (
                <div key={t} className="border rounded-lg overflow-hidden">
                  <div className={`px-2 py-1 text-xs font-medium border-b ${cls}`}>
                    {list[0]?.tag_label || t} ({list.length})
                  </div>
                  <div className="divide-y">
                    {list.map((f) => (
                      <div key={f.file_id} className="px-2 py-1.5 flex items-center gap-2 hover:bg-slate-50 text-sm" data-testid={`compliance-file-row-${f.file_id}`}>
                        <FileText className="h-4 w-4 text-slate-400 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-slate-800 truncate">{f.filename}</div>
                          <div className="text-xs text-slate-500 flex flex-wrap gap-2">
                            <span>{_formatBytes(f.file_size)}</span>
                            <span>{new Date(f.uploaded_at).toLocaleDateString("sl-SI")}</span>
                            {f.uploaded_by && <span>· {f.uploaded_by.split("@")[0]}</span>}
                            {f.version_label && <span className="font-medium text-emerald-700">· {f.version_label}</span>}
                            {f.description && <span className="italic">— {f.description}</span>}
                          </div>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => handleDownload(f)} title="Download">
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(f)} title="Delete" className="text-red-600 hover:text-red-700 hover:bg-red-50">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
